import { memo, useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import CustomTable from '../../../../shared/components/CustomTable';
import TruncatedText from '../../../../shared/components/TruncatedText';
import type { Contact, ContactFields, ContactGroup, ContactGroupOption } from '../../../../types/InfrastructureManagerTypes';
import { ContactAction } from '../../../../enums/contactActions';
import { ContactActionsCell } from './ContactActionsCell';
import styles from './ContactGrid.module.scss';

interface Props {
  group: ContactGroup;
  groupOptions: ContactGroupOption[];
  onEdit: (contact: Contact) => void;
  onMove: (contact: Contact, fromGroupId: number, toGroupId: number) => void;
  onDuplicate: (contact: Contact, targetGroupIds: number[]) => void;
  onDelete: (contact: Contact, groupId: number, anchor: HTMLElement | null) => void;
}

const TABLE_LOCALE = { emptyText: <p className={styles.emptyState}>No contacts assigned to group</p> };

const fields: { key: keyof ContactFields; title: string; width: number }[] = [
  { key: 'name', title: 'NAME', width: 180 },
  { key: 'email', title: 'EMAIL', width: 230 },
  { key: 'phone', title: 'PHONE NUMBER', width: 170 },
  { key: 'company', title: 'COMPANY', width: 190 },
  { key: 'role', title: 'ROLE', width: 170 },
  { key: 'notes', title: 'NOTES', width: 260 },
];

const ContactGridComponent = ({ group, groupOptions, onEdit, onMove, onDuplicate, onDelete }: Props) => {
  const targetOptions = useMemo(
    () => groupOptions.filter((target) => target.id !== group.id).map((target) => ({ id: String(target.id), label: target.name })),
    [groupOptions, group.id],
  );
  const rowOptions = useMemo(
    () => [
      { id: ContactAction.EDIT, label: 'EDIT CONTACT' },
      {
        id: ContactAction.MOVE,
        label: 'MOVE CONTACT',
        panel: { header: 'MOVE CONTACT TO', actionId: ContactAction.MOVE, showBack: true, options: targetOptions },
      },
      {
        id: ContactAction.DUPLICATE,
        label: 'DUPLICATE CONTACT',
        panel: { header: 'DUPLICATE CONTACT TO', actionId: ContactAction.DUPLICATE, showBack: true, multiSelect: true, options: targetOptions },
      },
      { id: ContactAction.DELETE, label: 'DELETE CONTACT' },
    ],
    [targetOptions],
  );

  const columns: ColumnsType<Contact> = useMemo(
    () => [
      ...fields.map(({ key, title, width }) => ({
        title,
        dataIndex: key,
        key,
        width,
        sorter: (left: Contact, right: Contact) => (left[key] ?? '').localeCompare(right[key] ?? ''),
        ellipsis: { showTitle: false },
        render: (value: ContactFields[typeof key]) => <TruncatedText text={value || '—'} />,
      })),
      {
        title: '',
        key: 'actions',
        width: 52,
        fixed: 'right',
        render: (_value, record) => (
          <ContactActionsCell
            contact={record}
            groupId={group.id}
            options={rowOptions}
            onEdit={onEdit}
            onMove={onMove}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
          />
        ),
      },
    ],
    [group.id, onDelete, onDuplicate, onEdit, onMove, rowOptions],
  );

  return (
    <CustomTable<Contact>
      className={styles.table}
      rowKey="id"
      dataSource={group.contacts}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 1252, y: Math.min(Math.max(group.contacts.length, 1), 5) * 56 }}
      locale={TABLE_LOCALE}
      showSorterTooltip={false}
    />
  );
};

export const ContactGrid = memo(ContactGridComponent);
